/**
 * Count Mode Offline Service
 *
 * Manages the lifecycle of lightweight count packages:
 *  1. Download count package (API → IndexedDB)
 *  2. Local count entry CRUD (read/write IndexedDB)
 *  3. Sync counts back to central (IndexedDB → API)
 */

import offlineDb from './offlineDb';
import api from './api';

const STORE = offlineDb.STORES.COUNT_PACKAGES;

export const COUNT_SYNC_STATES = {
  DOWNLOADED: 'downloaded',       // Package downloaded, counting not started
  COUNTING: 'counting',           // Active counting in progress
  READY_FOR_TRANSFER: 'ready_for_transfer', // Counting done, ready to transfer/sync
  SYNCING: 'syncing',             // Currently syncing to server
  SYNCED: 'synced',               // Successfully synced to server
  IMPORTED: 'imported',           // Imported to another device (awaiting server sync)
  PARTIAL: 'partial',             // Partial sync — some lines failed
  CONFLICT: 'conflict',           // Server conflict detected
  ERROR: 'error',                 // Sync/transfer error
};

/** Generate a stable device fingerprint for audit trail */
function getDeviceId() {
  let id = localStorage.getItem('az_device_id');
  if (!id) {
    id = `${navigator.userAgent.slice(0, 30).replace(/[^a-zA-Z0-9]/g, '')}-${Date.now().toString(36)}`;
    localStorage.setItem('az_device_id', id);
  }
  return id;
}

/** Record an audit event on the count package */
async function recordAuditEvent(sessionId, eventType, details = {}) {
  const stored = await offlineDb.get(STORE, sessionId);
  if (!stored) return;
  const trail = stored.audit_trail || [];
  trail.push({
    event: eventType,
    timestamp: new Date().toISOString(),
    device_id: getDeviceId(),
    device_label: `${navigator.platform || 'unknown'} / ${navigator.userAgent.includes('Mobile') ? 'Tablet' : 'Desktop'}`,
    user_id: details.user_id || null,
    user_name: details.user_name || null,
    session_version: stored.package?.session?.version || null,
    ...details,
  });
  stored.audit_trail = trail;
  await offlineDb.put(STORE, stored);
}

export { getDeviceId, recordAuditEvent };

/**
 * Download a lightweight count package for offline counting.
 */
export async function downloadCountPackage(sessionId) {
  try {
    // Preserve existing local counts BEFORE overwriting
    let preservedLocalCounts = {};
    try {
      const existing = await offlineDb.get(STORE, sessionId);
      if (existing && existing.local_counts) {
        preservedLocalCounts = { ...existing.local_counts };
      }
    } catch (_) { /* no existing package, nothing to preserve */ }

    const response = await api.get(`/count/sessions/${sessionId}/package`);
    const pkg = response.data;

    // Build server-side counts from existing entries
    const serverCounts = {};
    for (const line of (pkg.count_sheet_lines || [])) {
      if (line.existing_count) {
        serverCounts[line.id] = {
          quantity: line.existing_count.quantity,
          unit_id: line.existing_count.unit_id,
          unit_label: line.existing_count.unit_label,
          notes: line.existing_count.notes || '',
        };
      }
    }

    // Merge: local counts take precedence over server counts (may be unsynced)
    const mergedCounts = { ...serverCounts, ...preservedLocalCounts };

    // Determine sync state: if there are local counts not yet on server, keep COUNTING
    const hasUnsynced = Object.keys(preservedLocalCounts).some(
      lid => !serverCounts[lid] || JSON.stringify(preservedLocalCounts[lid]) !== JSON.stringify(serverCounts[lid])
    );

    const syncState = hasUnsynced ? COUNT_SYNC_STATES.COUNTING
      : Object.keys(preservedLocalCounts).length > 0 ? COUNT_SYNC_STATES.COUNTING
      : COUNT_SYNC_STATES.DOWNLOADED;

    await offlineDb.put(STORE, {
      id: sessionId,
      package: pkg,
      local_counts: mergedCounts,
      sync_state: syncState,
      audit_trail: [],
      downloaded_at: pkg.downloaded_at,
      server_session_updated_at: pkg.server_session_updated_at,
      session_reference: pkg.session?.session_reference,
      session_date: pkg.session?.session_date,
      site_name: pkg.session?.site_name,
      device_id: getDeviceId(),
    });

    await recordAuditEvent(sessionId, 'downloaded', { outcome: 'success' });

    return { success: true, package: pkg };
  } catch (error) {
    console.error('Failed to download count package:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get a stored count package from IndexedDB.
 */
export async function getCountPackage(sessionId) {
  return offlineDb.get(STORE, sessionId);
}

/**
 * Get all stored count packages.
 */
export async function getAllCountPackages() {
  return offlineDb.getAll(STORE);
}

/**
 * Save a single count entry locally.
 */
export async function saveLocalCount(sessionId, lineId, countData) {
  const stored = await offlineDb.get(STORE, sessionId);
  if (!stored) throw new Error('Count package not found');

  const localCounts = stored.local_counts || {};
  // iter365 — Splits are an optional UI-mode array of per-shelf counts.
  // When present, `quantity` is their sum (computed by the caller). We
  // persist splits alongside so reloading the tablet preserves the user's
  // breakdown across shelves / locations.
  localCounts[lineId] = {
    quantity: countData.quantity,
    unit_id: countData.unit_id,
    unit_label: countData.unit_label,
    notes: countData.notes || '',
    ...(Array.isArray(countData.splits) && countData.splits.length > 0
      ? { splits: countData.splits.map(s => ({
          qty: s.qty ?? '',
          // iter384 — preserve per-split unit override across reloads.
          unit_id: s.unit_id ?? null,
          _key: s._key || null,
        })) }
      : {}),
  };

  stored.local_counts = localCounts;
  stored.sync_state = COUNT_SYNC_STATES.COUNTING;
  await offlineDb.put(STORE, stored);
  return stored;
}

/**
 * Bulk-save all local counts at once (used for WAL recovery / flush).
 */
export async function saveAllLocalCounts(sessionId, allCounts) {
  const stored = await offlineDb.get(STORE, sessionId);
  if (!stored) throw new Error('Count package not found');
  stored.local_counts = { ...(stored.local_counts || {}), ...allCounts };
  stored.sync_state = COUNT_SYNC_STATES.COUNTING;
  await offlineDb.put(STORE, stored);
}

/**
 * Clear a single count entry locally.
 */
export async function clearLocalCount(sessionId, lineId) {
  const stored = await offlineDb.get(STORE, sessionId);
  if (!stored) return;

  const localCounts = stored.local_counts || {};
  delete localCounts[lineId];
  stored.local_counts = localCounts;
  await offlineDb.put(STORE, stored);
}

/**
 * Sync local counts back to the server.
 * @param {number} sessionId
 * @param {Object} options
 * @param {number[]} [options.retryLineIds] - Only sync these specific line IDs (for retry)
 */
export async function syncCounts(sessionId, options = {}) {
  const stored = await offlineDb.get(STORE, sessionId);
  if (!stored) throw new Error('Count package not found');

  const localCounts = stored.local_counts || {};
  let entries = Object.entries(localCounts).filter(([, v]) => v.quantity != null);

  // If retrying specific lines, filter to those
  if (options.retryLineIds && options.retryLineIds.length > 0) {
    const retrySet = new Set(options.retryLineIds);
    entries = entries.filter(([lineId]) => retrySet.has(parseInt(lineId, 10)));
  }

  const allCountEntries = entries.map(([lineId, data]) => ({
    count_sheet_line_id: parseInt(lineId, 10),
    quantity: data.quantity,
    unit_id: data.unit_id,
    unit_label: data.unit_label,
    notes: data.notes || null,
  }));

  // Mark syncing
  stored.sync_state = COUNT_SYNC_STATES.SYNCING;
  await offlineDb.put(STORE, stored);

  // Chunk sync to avoid 524 Cloudflare timeout on large stock lists
  const CHUNK_SIZE = 40;
  let totalSynced = 0;
  let totalSkipped = 0;
  let allFailures = [];
  let lastServerUpdatedAt = stored.server_session_updated_at;
  let hadConflict = false;
  let conflictDetails = null;

  try {
    const chunks = [];
    for (let i = 0; i < allCountEntries.length; i += CHUNK_SIZE) {
      chunks.push(allCountEntries.slice(i, i + CHUNK_SIZE));
    }
    // If no entries, still send one empty sync to mark_complete
    if (chunks.length === 0) chunks.push([]);

    for (let ci = 0; ci < chunks.length; ci++) {
      const chunk = chunks[ci];
      const isLastChunk = ci === chunks.length - 1;

      const response = await api.post(`/count/sessions/${sessionId}/sync`, {
        server_session_updated_at: lastServerUpdatedAt,
        count_entries: chunk,
        mark_complete: isLastChunk && !options.retryLineIds,
        force_overwrite: options.forceOverwrite || false,
        uncounted_only: options.uncountedOnly || false,
      });

      const result = response.data;

      if (result.status === 'conflict') {
        hadConflict = true;
        conflictDetails = result;
        break;
      }

      totalSynced += result.synced || 0;
      totalSkipped += result.skipped || 0;
      allFailures = allFailures.concat(result.failures || []);
      if (result.server_updated_at) {
        lastServerUpdatedAt = result.server_updated_at;
      }

      // Report progress via callback if provided
      if (options.onProgress) {
        options.onProgress({
          chunk: ci + 1,
          totalChunks: chunks.length,
          synced: totalSynced,
          total: allCountEntries.length,
        });
      }
    }

    if (hadConflict) {
      stored.sync_state = COUNT_SYNC_STATES.CONFLICT;
      stored.conflict_details = conflictDetails;
      await offlineDb.put(STORE, stored);
      return { success: false, conflict: true, details: conflictDetails };
    }

    if (allFailures.length > 0) {
      stored.sync_state = COUNT_SYNC_STATES.PARTIAL;
      stored.sync_failures = allFailures;
      stored.server_session_updated_at = lastServerUpdatedAt;
      stored.last_synced_at = new Date().toISOString();
      await offlineDb.put(STORE, stored);
      return { success: true, partial: true, synced: totalSynced, skipped: totalSkipped, failures: allFailures };
    }

    // Full success
    stored.sync_state = COUNT_SYNC_STATES.SYNCED;
    stored.sync_failures = [];
    stored.server_session_updated_at = lastServerUpdatedAt;
    stored.last_synced_at = new Date().toISOString();
    await offlineDb.put(STORE, stored);

    return { success: true, synced: totalSynced, skipped: totalSkipped, failures: [] };
  } catch (error) {
    stored.sync_state = COUNT_SYNC_STATES.ERROR;
    stored.sync_error = error.message;
    await offlineDb.put(STORE, stored);
    return { success: false, error: error.message };
  }
}

/**
 * Remove a count package from IndexedDB.
 */
export async function removeCountPackage(sessionId) {
  return offlineDb.remove(STORE, sessionId);
}

/**
 * Mark a counting session as ready for transfer (counting complete).
 */
export async function markReadyForTransfer(sessionId, userName) {
  const stored = await offlineDb.get(STORE, sessionId);
  if (!stored) throw new Error('Count package not found');
  stored.sync_state = COUNT_SYNC_STATES.READY_FOR_TRANSFER;
  stored.ready_at = new Date().toISOString();
  await offlineDb.put(STORE, stored);
  await recordAuditEvent(sessionId, 'marked_ready_for_transfer', { user_name: userName });
  return stored;
}

/**
 * Export a count package as a transferable JSON object.
 * Used for Bluetooth or QR transfer to another device.
 */
export async function exportCountPackage(sessionId) {
  const stored = await offlineDb.get(STORE, sessionId);
  if (!stored) throw new Error('Count package not found');

  const exportPayload = {
    _type: 'az_count_transfer',
    _version: 1,
    session_id: sessionId,
    session_reference: stored.session_reference,
    site_name: stored.site_name,
    local_counts: stored.local_counts || {},
    count_timestamp: new Date().toISOString(),
    source_device_id: getDeviceId(),
    source_device_label: `${navigator.platform || 'unknown'} / ${navigator.userAgent.includes('Mobile') ? 'Tablet' : 'Desktop'}`,
    lines_counted: Object.keys(stored.local_counts || {}).filter(k => stored.local_counts[k]?.quantity != null).length,
    total_lines: stored.package?.count_sheet_lines?.length || 0,
    server_session_updated_at: stored.server_session_updated_at,
    audit_trail: stored.audit_trail || [],
  };

  await recordAuditEvent(sessionId, 'exported_for_transfer', {
    lines_counted: exportPayload.lines_counted,
    method: 'package_export',
  });

  return exportPayload;
}

/**
 * Import a count package received from another device.
 * Merges into existing local data or creates new.
 */
export async function importCountPackage(transferPayload, userName) {
  if (!transferPayload || transferPayload._type !== 'az_count_transfer') {
    throw new Error('Invalid transfer package');
  }

  const sessionId = transferPayload.session_id;
  let stored = await offlineDb.get(STORE, sessionId);

  if (stored) {
    // Merge: incoming counts override local (transfer is the source of truth)
    const merged = { ...(stored.local_counts || {}), ...transferPayload.local_counts };
    stored.local_counts = merged;
    stored.sync_state = COUNT_SYNC_STATES.IMPORTED;
    stored.imported_at = new Date().toISOString();
    stored.imported_from_device = transferPayload.source_device_id;
  } else {
    // No local package — create a stub for import
    stored = {
      id: sessionId,
      package: null,
      local_counts: transferPayload.local_counts || {},
      sync_state: COUNT_SYNC_STATES.IMPORTED,
      imported_at: new Date().toISOString(),
      imported_from_device: transferPayload.source_device_id,
      session_reference: transferPayload.session_reference,
      site_name: transferPayload.site_name,
      server_session_updated_at: transferPayload.server_session_updated_at,
      device_id: getDeviceId(),
      audit_trail: [...(transferPayload.audit_trail || [])],
    };
  }

  await offlineDb.put(STORE, stored);
  await recordAuditEvent(sessionId, 'imported_from_transfer', {
    user_name: userName,
    source_device_id: transferPayload.source_device_id,
    source_device_label: transferPayload.source_device_label,
    lines_received: Object.keys(transferPayload.local_counts || {}).length,
    outcome: 'success',
  });

  return { success: true, session_id: sessionId, lines_imported: Object.keys(transferPayload.local_counts || {}).length };
}
